<template>
    <div class="row">
        <div v-if="!loading" class="col-9 d-flex align-items-center">
            <label :for="elementId" class="control-label sr-only">Voice</label>
            <select :id="elementId" v-model="voiceId"
                    class="form-control" name="voice"
                    style="width: 100%;"></select>
        </div>
        <div class="col-3 d-flex align-items-center">
            <button type="button" @click="testVoice()" class="btn btn-outline-dark mr-2" :disabled="loading"
                    aria-label="Test Voice">
                <i class="fa fa-headphones"></i>
            </button>
            <button type="button" @click="toggleSettings()" class="btn btn-outline-dark" :disabled="loading"
                    aria-label="Voice Settings">
                <i class="fa fa-cog"></i>
            </button>
        </div>
        <div v-if="showSettings" class="col-12 mt-3">
            <div class="card">
                <div class="card-body">
                    <div class="row">
                        <div class="col-6 mb-2">
                            <label :for="`speed-control-${idIndex}`" class="control-label">Speed</label>
                            <input
                                :id="`speed-control-${idIndex}`"
                                type="number"
                                class="form-control"
                                v-model="speed"
                                v-on:change="handleVoiceChange"
                                :min="minSpeed"
                                :max="maxSpeed"
                                step="0.1"
                            />
                        </div>
                        <template v-if="supportsPronunciationLexicon">
                            <div class="col-12 mb-2">
                                <label :for="`pronunciation-lexicon-${idIndex}`" class="control-label">
                                    Pronunciation Lexicon (JSON)
                                </label>
                                <input :id="`pronunciation-lexicon-${idIndex}`" ref="lexiconFileInput" type="file"
                                       accept="application/json,.json" class="form-control-file d-block"
                                       @change="onLexiconFileSelected"/>
                                <small class="form-text text-muted">
                                    Phrase-to-IPA entries applied at playback; on-screen text stays unchanged.
                                </small>
                                <div v-if="customPronunciations.length" class="mt-1">
                                    <span class="badge badge-info">
                                        {{ customPronunciations.length }} phrases loaded
                                    </span>
                                    <button type="button" class="btn btn-link btn-sm" @click="clearLexicon">
                                        Clear
                                    </button>
                                </div>
                                <div v-if="lexiconError" class="text-danger small mt-1">{{ lexiconError }}</div>
                            </div>
                        </template>
                        <template v-if="ttsProvider === TTS_PROVIDERS.ELEVENLABS">
                            <div class="col-6 mb-2">
                                <label :for="`tts-model-${idIndex}`" class="control-label">Model</label>
                                <select :id="`tts-model-${idIndex}`" class="form-control" v-model="modelId"
                                        v-on:change="handleVoiceChange">
                                    <option value="eleven_multilingual_v2">eleven_multilingual_v2</option>
                                    <option value="eleven_flash_v2_5">eleven_flash_v2_5</option>
                                    <option value="eleven_turbo_v2_5">eleven_turbo_v2_5</option>
                                    <option value="eleven_turbo_v2">eleven_turbo_v2</option>
                                    <option value="eleven_flash_v2">eleven_flash_v2</option>
                                    <option value="eleven_english_sts_v2">eleven_english_sts_v2</option>
                                    <option value="eleven_multilingual_v1">eleven_multilingual_v1</option>
                                    <option value="eleven_multilingual_sts_v2">eleven_multilingual_sts_v2</option>
                                    <option value="eleven_monolingual_v1">eleven_monolingual_v1</option>
                                </select>
                            </div>
                            <div class="col-6 mb-2">
                                <label :for="`stability-${idIndex}`" class="control-label">Stability</label>
                                <input :id="`stability-${idIndex}`" type="number" class="form-control"
                                       v-model="stability" v-on:change="handleVoiceChange"
                                       min="0.0" max="1.0" step="0.01"/>
                            </div>
                            <div class="col-6 mb-2">
                                <label :for="`similarity-boost-${idIndex}`" class="control-label">
                                    Similarity Boost
                                </label>
                                <input :id="`similarity-boost-${idIndex}`" type="number" class="form-control"
                                       v-model="similarityBoost" v-on:change="handleVoiceChange"
                                       min="0.0" max="1.0" step="0.01"/>
                            </div>
                            <div class="col-6 mb-2">
                                <label :for="`style-${idIndex}`" class="control-label">Style</label>
                                <input :id="`style-${idIndex}`" type="number" class="form-control"
                                       v-model="styleExaggeration" v-on:change="handleVoiceChange"
                                       min="0" max="10" step="1"/>
                            </div>
                            <div class="col-6 mb-2">
                                <label :for="`speaker-boost-${idIndex}`">Use Speaker Boost</label>
                                <input :id="`speaker-boost-${idIndex}`" type="checkbox" v-model="useSpeakerBoost"
                                       v-on:change="handleVoiceChange"/>
                            </div>
                            <div class="col-6 mb-2">
                                <label :for="`pronunciation-dictionary-${idIndex}`" class="control-label">
                                    Pronunciation Dictionary ID
                                </label>
                                <input :id="`pronunciation-dictionary-${idIndex}`" type="text" class="form-control"
                                       v-model="pronunciationDictionaryId" v-on:change="handleVoiceChange"
                                       placeholder="Optional"/>
                                <small class="form-text text-muted">
                                    Use alias rules; phoneme rules only apply on eleven_flash_v2 / eleven_v3.
                                </small>
                            </div>
                            <div class="col-6 mb-2">
                                <label :for="`pronunciation-dictionary-version-${idIndex}`" class="control-label">
                                    Pronunciation Dictionary Version ID
                                </label>
                                <input :id="`pronunciation-dictionary-version-${idIndex}`" type="text"
                                       class="form-control" v-model="pronunciationDictionaryVersionId"
                                       v-on:change="handleVoiceChange" placeholder="Optional (latest)"/>
                            </div>
                        </template>
                        <template v-if="ttsProvider === TTS_PROVIDERS.OPENAI">
                            <div class="col-12 mb-2">
                                <label :for="`instructions-${idIndex}`" class="control-label">Instructions</label>
                                <textarea :id="`instructions-${idIndex}`" class="form-control" v-model="instructions"
                                          v-on:change="handleVoiceChange" rows="3"></textarea>
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import {computed, onMounted, ref, watch} from 'vue';

const props = defineProps({
    id: String,
    type: String,
    idIndex: String,
    speed: Number,
    modelId: String,
    stability: Number,
    similarityBoost: Number,
    styleExaggeration: Number,
    useSpeakerBoost: Boolean,
    instructions: String,
    pronunciationDictionaryId: String,
    pronunciationDictionaryVersionId: String,
    customPronunciations: Array,
    assistantProvider: {type: String, default: null},
});

const emit = defineEmits(['handleChange']);
const loading = ref(true);
const showSettings = ref(false);

const voiceId = ref(props.id || null);
const ttsProvider = ref('google');
const languageCode = ref('en-US');
const voice = ref('en-US-Wavenet-A');
const speed = ref(parseFloat(props.speed) || 1.0);
const useSpeakerBoost = ref(props.useSpeakerBoost || false);
const modelId = ref(props.modelId || 'eleven_flash_v2_5');
const stability = ref(props.stability || 0.50);
const similarityBoost = ref(props.similarityBoost || 0.75);
const styleExaggeration = ref(props.styleExaggeration || 0);
const instructions = ref(props.instructions || '');
const pronunciationDictionaryId = ref(props.pronunciationDictionaryId || '');
const pronunciationDictionaryVersionId = ref(props.pronunciationDictionaryVersionId || '');
const customPronunciations = ref(Array.isArray(props.customPronunciations) ? [...props.customPronunciations] : []);
const lexiconError = ref('');
const lexiconFileInput = ref(null);

const elementId = ref((props.idIndex) ? `voice-select-${props.idIndex}` : 'ai-assistant-select');
const allVoices = ref([]);

const TTS_PROVIDERS = {
    GOOGLE: 'google',
    ELEVENLABS: 'elevenlabs',
    OPENAI: 'openai',
    BEDROCK: 'bedrock',
};

const toggleSettings = () => {
    showSettings.value = !showSettings.value;
};

const minSpeed = computed(function () {
    return ttsProvider.value === 'elevenlabs' ? 0.7 : 0.25;
});

const maxSpeed = computed(function() {
    return ttsProvider.value === 'elevenlabs' ? 1.2 : 4.0;
});

// Google only honors customPronunciations on Chirp 3 HD voices; hiding the control
// elsewhere keeps authors from uploading a lexicon that would silently do nothing.
const supportsPronunciationLexicon = computed(() =>
    ttsProvider.value === TTS_PROVIDERS.GOOGLE && /Chirp3-HD/i.test(voice.value || ''));

function filterVoicesByProvider(voices, assistantProvider) {
    if (!assistantProvider) return voices;
    return voices.filter(v => {
        const prefix = v.id.split('_')[0];
        const isOpenAIWithPolly = assistantProvider === 'openai' && prefix === 'polly';
        return !isOpenAIWithPolly;
    });
}

async function fetchVoices() {
    let voiceList = [];
    try {
        const response = await fetch('/api/tts/voices', {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
                'x-api-token': window.apiToken,
            },
        });
        const resp = response.ok ? await response.json() : [];
        voiceList = Array.isArray(resp) ? resp : [];
    } catch (error) {
        console.error('Failed to load TTS voices: ', error);
    }

    const voices = voiceList.map(item => ({
        id: item.voice,
        text: item.label,
        languageCode: item.languageCode,
    }));
    allVoices.value = voices;
    loading.value = false;
    return filterVoicesByProvider(voices, props.assistantProvider);
}

function updateVoicesByProvider() {
    const filteredVoices = filterVoicesByProvider(allVoices.value, props.assistantProvider);
    const $selectEl = $(`#${elementId.value}`);

    if ($selectEl.length && $selectEl.data('select2')) {
        const data = _.map(filteredVoices, (tag) => ({
            id: tag.id,
            text: tag.text,
            languageCode: tag.languageCode || 'en-US',
        }));

        // Clear selection if current voice is not in filtered list
        const isCurrentVoiceValid = filteredVoices.some(v => v.id === voiceId.value);
        if (!isCurrentVoiceValid) {
            voiceId.value = null;
            $selectEl.val(null);
        }

        $selectEl.select2('destroy');
        $selectEl.empty();
        $selectEl.select2({data: data});
    }
}

function buildPronunciationDictionaryLocators() {
    const dictionaryId = (pronunciationDictionaryId.value || '').trim();
    if (!dictionaryId) {
        return [];
    }

    const versionId = (pronunciationDictionaryVersionId.value || '').trim();
    const locator = {pronunciation_dictionary_id: dictionaryId};
    if (versionId) {
        locator.version_id = versionId;
    }

    return [locator];
}

const LEXICON_PHONETIC_ENCODINGS = ['PHONETIC_ENCODING_IPA', 'PHONETIC_ENCODING_X_SAMPA'];

function normalizeLexicon(parsed) {
    const rows = Array.isArray(parsed) ? parsed : parsed?.pronunciations;
    if (!Array.isArray(rows) || rows.length === 0) {
        throw new Error('JSON must contain a non-empty "pronunciations" array.');
    }

    return rows.map((row, index) => {
        if (!row?.phrase || !row?.pronunciation) {
            throw new Error(`Entry ${index + 1} needs both "phrase" and "pronunciation".`);
        }

        const encoding = row.phonetic_encoding || 'PHONETIC_ENCODING_IPA';
        if (!LEXICON_PHONETIC_ENCODINGS.includes(encoding)) {
            throw new Error(`Entry ${index + 1} has unsupported phonetic_encoding "${encoding}".`);
        }

        return {
            phrase: String(row.phrase),
            pronunciation: String(row.pronunciation),
            phonetic_encoding: encoding,
        };
    });
}

async function onLexiconFileSelected(event) {
    lexiconError.value = '';
    const file = event.target.files?.[0];
    if (!file) {
        return;
    }

    try {
        customPronunciations.value = normalizeLexicon(JSON.parse(await file.text()));
    } catch (e) {
        customPronunciations.value = [];
        lexiconError.value = e instanceof SyntaxError
            ? 'File is not valid JSON.'
            : (e.message || 'Invalid lexicon file.');
        // Reset only on failure so the label doesn't display a file that didn't load,
        // and re-selecting the same file after fixing it still fires change. On
        // success the browser keeps showing the chosen filename.
        resetLexiconFileInput();
    }

    handleVoiceChange();
}

function resetLexiconFileInput() {
    if (lexiconFileInput.value) {
        lexiconFileInput.value.value = '';
    }
}

function clearLexicon() {
    customPronunciations.value = [];
    lexiconError.value = '';
    resetLexiconFileInput();
    handleVoiceChange();
}

function buildTestVoiceOpts() {
    if (ttsProvider.value === TTS_PROVIDERS.OPENAI) {
        return {instructions: instructions.value};
    }

    if (ttsProvider.value === TTS_PROVIDERS.ELEVENLABS) {
        const locators = buildPronunciationDictionaryLocators();
        return locators.length ? {pronunciation_dictionary_locators: locators} : {};
    }

    if (ttsProvider.value === TTS_PROVIDERS.GOOGLE) {
        return supportsPronunciationLexicon.value && customPronunciations.value.length
            ? {custom_pronunciations: customPronunciations.value}
            : {};
    }

    return {};
}

function buildTestVoiceText() {
    // Embed lexicon phrases so the author hears the override, not just a generic line.
    if (supportsPronunciationLexicon.value && customPronunciations.value.length) {
        const phrases = customPronunciations.value.slice(0, 2).map(row => row.phrase).join(' and ');
        return `Good morning, let's talk about ${phrases}.`;
    }

    return 'Good morning, how are you today?';
}

async function testVoice() {
    const response = await fetch('/api/tts/generate-audio', {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            'x-api-token': window.apiToken,
        },
        body: JSON.stringify({
            text: buildTestVoiceText(),
            languageCode: languageCode.value,
            voice: voice.value,
            provider: ttsProvider.value,
            speed: speed.value,
            opts: buildTestVoiceOpts(),
        }),
    });

    if (!response.ok) {
        const body = await response.json().catch(() => ({}));

        const validationErrors = body.errors
            ? Object.values(body.errors).flat().join('\n')
            : '';
        alert(validationErrors || body.error || `Error: ${response.status} ${response.statusText}`);
        return;
    }

    const audio = await new Response(response.body).blob();

    const file = new File([audio], 'recording.mp3', {type: 'audio/mp3'});
    const audioUrl = URL.createObjectURL(file);

    try {
        const audioElement = new Audio();
        audioElement.src = audioUrl;
        audioElement.play().catch((error) => {
            console.error('Failed to play audio: ', error);
        });
    } catch (error) {
        console.error('Failed to play audio: ', error);
    }
}

const handleVoiceChange = () => {
    const opts = {
        model_id: modelId.value,
        stability: stability.value,
        similarity_boost: similarityBoost.value,
        style_exaggeration: styleExaggeration.value,
        use_speaker_boost: useSpeakerBoost.value,
        instructions: instructions.value,
    };

    // Emit the locator shape the API expects rather than flat ID/version fields, so
    // every consumer persists one canonical shape. Omitted when unset to keep saved
    // voice opts clean and let the env-level default apply.
    const locators = buildPronunciationDictionaryLocators();
    if (locators.length) {
        opts.pronunciation_dictionary_locators = locators;
    }

    if (supportsPronunciationLexicon.value && customPronunciations.value.length) {
        opts.custom_pronunciations = customPronunciations.value;
    }

    emit('handleVoiceChange', {
        voiceId: voiceId.value,
        index: props.idIndex,
        elementId: elementId.value,
        speed: speed.value,
        opts,
    });
};

onMounted(async () => {
    const voices = await fetchVoices();

    $(() => {
        const $selectEl = $(`#${elementId.value}`);
        const data = _.map(voices, (tag) => {
            const isSelected = tag.id.split('_')[1] === voiceId.value;
            if (isSelected) {
                voiceId.value = tag.id;
                ttsProvider.value = tag.id.split('_')[0];
                voice.value = tag.id.split('_')[1];
                languageCode.value = tag.languageCode || 'en-US';
            }
            return {
                id: tag.id,
                text: tag.text,
                selected: isSelected,
                languageCode: tag.languageCode || 'en-US',
            };
        });
        $selectEl.select2({
            data: data,
        }).on('change', function () {
            const selected = $selectEl.select2('data');
            const split = selected[0].id.split('_');
            const langCode = data.find(d => d.id === selected[0].id);
            ttsProvider.value = split[0];
            voice.value = split[1];
            languageCode.value = langCode?.languageCode || 'en-US';
            voiceId.value = selected[0].id;
            handleVoiceChange();
        });
    });
});

watch(() => props.assistantProvider, () => {
    updateVoicesByProvider();
});

// Re-sync when the parent rebinds this instance to a different voice config
// (character delete/reorder without a stable :key), so a lexicon never ghosts
// from one character onto another.
watch(() => props.customPronunciations, (value) => {
    customPronunciations.value = Array.isArray(value) ? [...value] : [];
});
</script>
